import { CheckoutService } from './../../api/services/checkout.service';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, of } from 'rxjs';
import { CartProductModel } from 'src/app/api/models/checkout';
import { ProfileDataRequest } from 'src/app/api/models/perfil';
import { Bonus, QuantityCampaignDetails, ShopItemModel } from 'src/app/api/models/shop-item.model';
import { CartService } from 'src/app/api/services/cart.service';
import { PerfilService } from 'src/app/api/services/perfil.service';
import { environment } from 'src/environments/environment';
import { SnackBarService } from 'src/app/api/services/snack-bar.service';

interface BonusData {
  referenceBonus: string,
  imageURL: string,
  bonuses: Bonus[]
}

export interface DialogData{
  bonusData: ShopItemModel,
  addButton: boolean,
  quantityCampaign?:QuantityCampaignDetails;
  processChanges?: boolean;
}

@Component({
    selector: 'app-product-bonus-modal',
    templateUrl: './product-bonus-modal.component.html',
    styleUrls: ['./product-bonus-modal.component.css'],
    standalone: false
})
export class ProductBonusModalComponent {
  private loggedUser!: ProfileDataRequest;
  private shopCart = new BehaviorSubject<CartProductModel[]>([]);
  public bonusGroups = new Map<string, Bonus[]>();
  bonusData: BonusData[] = []
  passedData : ShopItemModel = this.data.bonusData;
  shouldShowAddButton = this.data.addButton;
  
  constructor(
    public dialogRef: MatDialogRef<ProductBonusModalComponent>,
    private checkoutService: CheckoutService,
    private perfilService: PerfilService,
    private cartService: CartService,
    private snackBarService: SnackBarService,

    @Inject(MAT_DIALOG_DATA) public data: DialogData) {}

  ngOnInit(): void {
    this.bonusData = this.getImageProductBonus()
    this.setOverlayZIndex();
  }

  setOverlayZIndex(): void {
    const overlayContainer = document.querySelector('.cdk-overlay-container') as HTMLElement;
    if (overlayContainer) {
      overlayContainer.style.zIndex = '10000';
    }
  }

  closeModal() {
    this.dialogRef.close();
  }

  getImageProductBonus() {
    if (this.passedData.bonus && this.passedData.bonus.length > 0) {
      this.bonusGroups = new Map<string, Bonus[]>();
      for (const bonus of this.passedData.bonus) {
        const referenceBonus = bonus.referenceBonus;
        if (!this.bonusGroups.has(referenceBonus)) {
          this.bonusGroups.set(referenceBonus, []);
        }
        const bonusGroup = this.bonusGroups.get(referenceBonus);
        if (bonusGroup) {
          bonusGroup.push(bonus);
        }
      }

      // Return an array of objects containing referenceBonus, image URL, and bonuses
      return Array.from(this.bonusGroups.entries()).map(([referenceBonus, bonuses]) => {
        const imageURL = environment.shop_images + bonuses[0].imageBonus;
        return {
          referenceBonus: referenceBonus,
          imageURL: imageURL,
          bonuses: bonuses
        };
      });
    }
    return [];
  }

  callCrudCart(data: ShopItemModel, bonus: string) {  
    let quantityToAdd = 0;

    const bonusSplit = bonus.split('+');
    const requiredQtyForBonus = parseInt(bonusSplit[0], 10);

    let currentQtd = 0;
    if (data.productQuantity) {
      currentQtd = data.productQuantity;
    }

    if (currentQtd % requiredQtyForBonus === 0) {
      // Already at a bonus threshold, add a full group
      quantityToAdd = requiredQtyForBonus;
    } else {
      // Add only what’s needed to reach the next threshold
      
      // Step 1: Parse and build all tiers
      const bonusTiers = data.bonus
        .map(b => {
          const [requiredStr, givenStr] = b.bonus.split('+');
          const required = parseInt(requiredStr, 10);
          const given = parseInt(givenStr, 10);
          return (!isNaN(required) && !isNaN(given)) ? {
            requiredProductQuantity: required,
            bonusProductQuantityGiven: given,
            rawBonus: b.bonus
          } : null;
        })
        .filter((b): b is { requiredProductQuantity: number; bonusProductQuantityGiven: number; rawBonus: string } => !!b);

      // Step 2: Sort tiers descending
      const sortedTiers = bonusTiers.sort((a, b) => b.requiredProductQuantity - a.requiredProductQuantity);

      let remainingQtd = currentQtd;

      // Step 3: Subtract units consumed by higher tiers
      for (const tier of sortedTiers) {
        if (tier.rawBonus === bonus) break;

        const applicableBonusCount = Math.floor(remainingQtd / tier.requiredProductQuantity);
        const consumed = applicableBonusCount * tier.requiredProductQuantity;
        remainingQtd -= consumed;
      }

      // Step 4: Compute what's needed to get 1 bonus for the selected tier
      const selectedTier = sortedTiers.find(t => t.rawBonus === bonus);
      if (selectedTier) {
        const remainder = remainingQtd % selectedTier.requiredProductQuantity;
        if (remainder === 0) {
          quantityToAdd = selectedTier.requiredProductQuantity;
        } else {
          quantityToAdd = selectedTier.requiredProductQuantity - remainder;
        }
      }
    }

    // Check if it should process changes here or if the changes should be handled in the parent component 
    if (!this.data.processChanges) {
      // Instead of adding to the cart, just close with result
      this.dialogRef.close({
        reference: data.reference,
        quantityToAdd: quantityToAdd
      });
      return;
    }

    // Default logic (for other components)
    this.loggedUser = this.perfilService.getCurrentUserInfo();
    let shopcard = this.cartService.getShopCart();
    const currentShopCart = shopcard.getValue();
    const updatedCart = [...currentShopCart];
    let existingProduct = updatedCart.find(p => p.productId === data.reference && p.usePoints === false && p.previouslyUsingPoints === false);
    let itemSelected: CartProductModel;
    if (existingProduct) {
      existingProduct.quantity += quantityToAdd;
      itemSelected = existingProduct;
    } else {
      itemSelected = {
        userId: this.loggedUser.userId,
        clientId: this.loggedUser.clientId,
        productId: data.reference,
        quantity: quantityToAdd,
        previouslyUsingPoints: false,
        usePoints: false,
        points: 0
      };
      updatedCart.push(itemSelected);
    }
    this.shopCart.next(updatedCart);
    this.cartService.updateBackendCart(itemSelected, false).subscribe(opResult => {
      if (opResult) {
        this.cartService.loadCart();
        this.snackBarService.openSnackBar(
          'Produto adicionado ao carrinho!',
          '#0AA174',
          '../../assets/Icons/correct.png'
        );
      }
    });
    this.checkoutService.CrudCartProduct(itemSelected);

    this.dialogRef.close({
      reference: data.reference,
      quantityToAdd: quantityToAdd
    });
  }

  hasQuantityCampaign(): boolean {
    return this.data?.bonusData?.quantityCampaign?.some(qc => qc.quantity > 0 && qc.discountPercentage > 0) ?? false;
  }
  
}
